/**
 * Mensagem amigável para erros não estruturados (rede, HTTP, backend genérico).
 * Erros de validação por campo vêm de `parseApiValidationErrors` — não use isto para sobrescrever fieldErrors.
 */

const FRIENDLY_MESSAGES: Record<number, string> = {
  400: 'Os dados enviados estão incorretos. Verifique e tente novamente.',
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Você não tem permissão para esta ação.',
  404: 'Recurso não encontrado.',
  408: 'A requisição demorou muito. Tente novamente.',
  409: 'Conflito com dados existentes. Verifique e tente novamente.',
  422: 'Os dados enviados não puderam ser processados. Verifique e tente novamente.',
  429: 'Muitas tentativas. Aguarde um momento e tente novamente.',
  500: 'Erro no servidor. Tente novamente em instantes ou contate o suporte.',
  502: 'Serviço temporariamente indisponível. Tente novamente em instantes.',
  503: 'Serviço temporariamente indisponível. Tente novamente em instantes.'
};

const NETWORK_ERROR_MESSAGE = 'Falha na conexão. Verifique sua internet e tente novamente.';
const GENERIC_ERROR_MESSAGE = 'Ocorreu um erro inesperado. Tente novamente ou contate o suporte.';

export function getFriendlyErrorMessage(error: unknown, fallback?: string): string {
  const defaultMessage = fallback ?? GENERIC_ERROR_MESSAGE;
  if (!error) return defaultMessage;

  const err = error as {
    response?: { status?: number; data?: { message?: string } };
    message?: string;
    code?: string;
  };
  const status = err.response?.status;
  const dataBody = err.response?.data as Record<string, unknown> | undefined;

  if (
    typeof dataBody?.detail === 'string' &&
    dataBody.detail.trim() &&
    (status === 400 || status === 422)
  ) {
    return dataBody.detail.trim();
  }

  const friendlyForStatus = status ? FRIENDLY_MESSAGES[status] : undefined;
  if (friendlyForStatus) return friendlyForStatus;

  const isNetworkError =
    err.code === 'ERR_NETWORK' ||
    err.code === 'ERR_SOCKET_NOT_CONNECTED' ||
    err.code === 'ERR_CONNECTION_REFUSED' ||
    err.code === 'ERR_CONNECTION_RESET' ||
    err.code === 'ECONNABORTED' ||
    err.message?.includes('Network Error') ||
    err.message?.includes('ECONNREFUSED');

  if (isNetworkError) return NETWORK_ERROR_MESSAGE;

  const backendMessage = err.response?.data?.message;
  if (typeof backendMessage === 'string' && backendMessage.trim()) {
    const technicalPatterns = [
      /^[\w\s]+Error:/i,
      /at\s+\S+\s*\(/,
      /stack\s*trace/i,
      /undefined is not/i,
      /cannot read property/i,
      /token/i,
      /jwt/i,
      /mongoose/i,
      /mongo/i,
      /ECONNREFUSED/i,
      /ETIMEDOUT/i,
      /EAI_AGAIN/i,
      /querySrv/i,
      /ESERVFAIL/i,
      /ENOTFOUND/i,
      /getaddrinfo/i,
      /_tcp\.\S+\.(mongodb|net)/i
    ];
    const isTechnical = technicalPatterns.some(p => p.test(backendMessage));
    if (!isTechnical && backendMessage.length < 200) return backendMessage;
  }

  return defaultMessage;
}
